import { ClosedLostReason, Opportunity } from './opportunity';
import { Observable } from 'rxjs';
import { GetTagsRequest, GetTagsResponse } from '../filters/tag-selector/interface';
import { DurationPeriod, Opportunity as SalesOpportunity } from '@vendasta/sales-opportunities';

const BASE_URL = '/v1/opportunity/';
export const CREATE_OPPORTUNITY_URL = BASE_URL + 'create';
export const GET_TAGS_URL = BASE_URL + 'get/tags';
export const UPDATE_TAGS_URL = BASE_URL + 'update/tags';

export interface UpdateOpportunityTagsRequest {
  opportunityId: string;
  accountGroupId: string;
  editSalesPersonId: string;
  tags: string[];
}

export interface GeneralBusinessInfo {
  name: string;
  partnerId: string;
  marketId: string;
  streetAddress: string;
  city: string;
  state: string;
  countryCode: string;
  zip: string;
  phoneNumber: string;
  website: string;
  businessId: string;
  serviceCategories: string[];
  socialUrls: SocialURLs;
  vCategories: string[];
}

export interface OpportunityPackage {
  packageId: string;
  quantity: number;
}

export interface OpportunityValues {
  pipelineId: string;
  probability: number;
}

export interface CreateOpportunityRequest {
  accountGroupId: string;
  partnerId: string;
  marketId: string;
  salesPersonId: string;
  name: string;
  packages: OpportunityPackage[];
  expectedCloseDate: Date;
  probability: number;
  notes: string;
  tags: string[];
}

export interface CreateOpportunityResponse {
  opportunity: Opportunity;
}

export interface UpdateOpportunityTagsResponse {
  opportunity: Opportunity;
}

export interface SalesOpportunitiesApi {
  create(req: CreateOpportunityRequest): Observable<CreateOpportunityResponse>;
  getTags(req: GetTagsRequest): Observable<GetTagsResponse>;
  updateTags(req: UpdateOpportunityTagsRequest): Observable<UpdateOpportunityTagsResponse>;
}

export interface ContractDurationTranslationKey {
  key: string;
  value: number;
}

export const contractDurationKeysToEnum: ContractDurationTranslationKey[] = [
  { key: 'SALES_CONTRACT_DURATION.DURATIONS.DAY', value: DurationPeriod.DAY },
  { key: 'SALES_CONTRACT_DURATION.DURATIONS.WEEK', value: DurationPeriod.WEEK },
  { key: 'SALES_CONTRACT_DURATION.DURATIONS.MONTH', value: DurationPeriod.MONTH },
  { key: 'SALES_CONTRACT_DURATION.DURATIONS.YEAR', value: DurationPeriod.YEAR },
];

export const contractDurationKeysToEnumSingular: ContractDurationTranslationKey[] = [
  { key: 'SALES_CONTRACT_DURATION.DURATIONS.DAY_SINGULAR', value: DurationPeriod.DAY },
  { key: 'SALES_CONTRACT_DURATION.DURATIONS.WEEK_SINGULAR', value: DurationPeriod.WEEK },
  { key: 'SALES_CONTRACT_DURATION.DURATIONS.MONTH_SINGULAR', value: DurationPeriod.MONTH },
  { key: 'SALES_CONTRACT_DURATION.DURATIONS.YEAR_SINGULAR', value: DurationPeriod.YEAR },
];

export enum UpdateRequestType {
  PROBABILITY,
  PIPELINE_ID,
}

export interface OpportunitiesCommands {
  updateProbability(opportunity: SalesOpportunity, newProbability: number): void;
  updatePipelineId(opportunity: SalesOpportunity, newPipelineId: string, newProbability: number): void;
}

export interface UpdateRequest extends SalesOpportunity {
  requestType: UpdateRequestType;
  probabilityIfUpdateFails?: number;
  pipelineIdIfUpdateFails?: string;
}

export enum CloseState {
  WON = 'won',
  LOST = 'lost',
}

export interface OpportunityCloseEvent {
  closeState: CloseState;
  reason: string;
  notes: string;
  actualClosedDate: Date;
}

export const ClosedLostReasonStringMapping = {
  price: ClosedLostReason.Price,
  'no-budget': ClosedLostReason.NoBudget,
  'lost-to-competitor': ClosedLostReason.LostToCompetitor,
  'not-ready': ClosedLostReason.NotReady,
  other: ClosedLostReason.Other,
};

export interface AccountTag {
  name: string;
  value: string;
}

export interface SocialURLs {
  linkedinUrl: string;
  twitterUrl: string;
  facebookUrl: string;
  instagramUrl: string;
}

export interface OpportunityType {
  id: string;
  displayName: string;
}

export const MIN_CONTRACT_DURATION_REGEX = '^[1-9][0-9]*?$'; // regex for checking minimum valid contract duration (1 year or month)
